import {
	MaintenanceSettingsInitializationModel,
	SaveMaintenanceSettingsModel
} from "client/Api/Apps/EditApp/saveApp";
import {
	Checkbox,
	DatePicker,
	FormItem,
	TextArea,
	ValidationError} from "client/Components";
import { IValueInputAttachable } from "client/Forms/attachable";
import { isNil, isNotNil, resolveFormattedTextString } from "client/Misc/utils";
import {
	createValidationConfiguration,
	InputValidationResult,
	InputValidationResultKind,
	IValidationConfiguration
} from "client/Validation";
import {
	doesNotExceedLength,
	inline,
	isNotNullNorUndefined,
	isValidDate
} from "client/Validation/rules";
import { ReactElement } from "react";
import { localize } from "client_legacy/ndaw/culture";
import { TimeZone } from "client/DateAndTime/timezones";
import { parseDtoDate } from "client/DateAndTime/dateTimeUtils";

export type MaintenanceWindowProps = {
	attachable: IValueInputAttachable<SaveMaintenanceSettingsModel>;
	timezone: TimeZone;
};

export const MaintenanceWindow = (props: MaintenanceWindowProps): ReactElement => {
	const {
		attachable,
		timezone
	} = props;

	return (
		<>
			<FormItem>
				<Checkbox
					{...attachable.for("isMaintenanceEnabled")
						.attachToCheckbox()
					}
					text={localize("_ScheduleMaintenance")}
				/>

				<ValidationError
					{...attachable
						.for("isMaintenanceEnabled")
						.attachToValidationError()
					}
				/>
			</FormItem>

			{attachable.value.isMaintenanceEnabled && (
				<>
					<FormItem label={localize("_StartsOn")}>
						<DatePicker
							{...attachable.for("startsOn").attachToDatePicker()}
							timezone={timezone}
						/>

						<ValidationError
							{...attachable.for("startsOn").attachToValidationError()}
						/>
					</FormItem>

					<FormItem label={localize("_EndsOn")}>
						<DatePicker
							{...attachable.for("endsOn").attachToDatePicker()}
							timezone={timezone}
						/>

						<ValidationError
							{...attachable.for("endsOn").attachToValidationError()}
						/>
					</FormItem>

					<FormItem label={localize("_NotificationMessage")}>
						<TextArea
							{...attachable.for("notificationMessage")
								.attachToTextArea()
							}
							rows={3}
							placeholder={{
								template: localize("_StandardIncludedText"),
								replacements: [
									{
										what: "{0}",
										filler: resolveFormattedTextString({
											template: localize("_TheAppIsInMaintenanceUntil"),
											replacements: [
												{ what: "{0}", filler: localize("_EndDateAndTime") }
											]
										})
									}
								]
							}}
						/>

						<ValidationError
							{...attachable
								.for("notificationMessage")
								.attachToValidationError()
							}
						/>
					</FormItem>
				</>
			)}
		</>
	);
};
MaintenanceWindow.displayName = "MaintenanceWindow";

const rootValidation = createValidationConfiguration<SaveMaintenanceSettingsModel>();

const validation = rootValidation.when(x => x.isMaintenanceEnabled);

validation.forScalar("startsOn")
	.add(
		isNotNullNorUndefined(),
		isValidDate()
	);

validation.forScalar("endsOn")
	.add(
		isNotNullNorUndefined(),
		isValidDate()
	);

validation
	.when(x => {
		return isNotNil(x.startsOn)
			&& !isNaN(x.startsOn.getTime())
			&& isNotNil(x.endsOn)
			&& !isNaN(x.endsOn.getTime())
			&& x.endsOn <= x.startsOn;
	})
	.forScalar("endsOn")
	.add(
		inline((): InputValidationResult => {
			return {
				kind: InputValidationResultKind.Invalid,
				errorMessage: localize("_EndDateTimeMustBeGreaterThanStartDateTime")
			};
		})
);

validation
	.forScalar("notificationMessage")
	.add(
		doesNotExceedLength(255)
	);

export function maintenanceWindowValidator(): IValidationConfiguration<SaveMaintenanceSettingsModel> {
	return rootValidation.clone();
}

export function initialMaintenanceWindowState(
	initialModel: MaintenanceSettingsInitializationModel | null
): SaveMaintenanceSettingsModel {
	const {
		isMaintenanceEnabled = false,
		startsOn = null,
		endsOn = null,
		notificationMessage = ""
	} = initialModel ?? {};

	return {
		isMaintenanceEnabled,
		startsOn: parseDtoDate(startsOn),
		endsOn: parseDtoDate(endsOn),
		notificationMessage
	};
}
export function prepareMaintenanceWindowForSaving(
	model: SaveMaintenanceSettingsModel
): SaveMaintenanceSettingsModel;
export function prepareMaintenanceWindowForSaving(
	model: SaveMaintenanceSettingsModel | null
): SaveMaintenanceSettingsModel | null;
export function prepareMaintenanceWindowForSaving(
	model: SaveMaintenanceSettingsModel | null
): SaveMaintenanceSettingsModel | null {
	if (isNil(model)) {
		return null;
	}

	const {
		isMaintenanceEnabled,
		startsOn,
		endsOn,
		notificationMessage
	} = model;

	return {
		isMaintenanceEnabled,
		startsOn: isMaintenanceEnabled
			? startsOn
			: null,
		endsOn: isMaintenanceEnabled
			? endsOn
			: null,
		notificationMessage: isMaintenanceEnabled
			? notificationMessage
			: null
	};
}
